import {
  Edit,
  Eye,
  EyeOff,
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmModal,
  Input,
  LoadingSpinner,
  Modal,
  SimpleWorkflowBuilder,
} from '../components/ui';
import Pagination from '../components/ui/Pagination';
import {
  CrewCreationProvider,
  useAuth,
  useCrewCreation,
  useOrganization,
} from '../contexts';
import {
  agentService,
  crewOrderService,
  crewService,
  taskService,
} from '../services';
import { Crew, CrewCategory } from '../types';

const AgentTeams: React.FC = () => {
  const { t } = useTranslation();
  const [crews, setCrews] = useState<Crew[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [crewToDelete, setCrewToDelete] = useState<number | null>(null);
  const [editingCrew, setEditingCrew] = useState<Crew | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState<CrewCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCrews, setTotalCrews] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [editingWorkflow, setEditingWorkflow] = useState<{
    agents: any[];
    tasks: any[];
    connections: { from: string; to: string }[];
    crewName?: string;
    crewDescription?: string;
    category_ids?: number[];
  } | null>(null);

  const { currentOrganization } = useOrganization();
  const { resetData, setIsCreating } = useCrewCreation();
  const { user } = useAuth();

  // Verificar se o usuário é superadmin pela role
  const isSuperAdmin = (): boolean => {
    if (!user?.roles || !Array.isArray(user.roles)) {
      return false;
    }
    return user.roles.includes('superadmin');
  };

  const loadData = useCallback(async () => {
    if (!currentOrganization) return;

    try {
      setIsLoading(true);
      const paginatedCrews = await crewService.getAll({
        organization_id: currentOrganization.id,
        category_id: selectedCategoryId || undefined,
        include_hidden: true,
        page: currentPage,
        page_size: pageSize,
      });

      setCrews(paginatedCrews.items || []);
      setTotalCrews(paginatedCrews.total);
      setTotalPages(Math.max(1, paginatedCrews.total_pages || 1));
    } catch (error) {
      toast.error(t('teams.errorLoadingData'));
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization, currentPage, pageSize, selectedCategoryId, t]);

  const loadCategories = useCallback(async () => {
    if (!currentOrganization) return;
    try {
      const list = await crewService.getCategories(currentOrganization.id);
      setCategories(list);
    } catch (error) {
      toast.error(t('teams.errorLoadingCategories'));
    }
  }, [currentOrganization, t]);

  const getTaskIdsForAgents = useCallback(
    async (organizationId: number, agentIds: number[]): Promise<number[]> => {
      const allTasks: Array<{ id: number; agent_id: number | null }> = [];
      let page = 1;
      let totalPagesToFetch = 1;

      do {
        const response = await taskService.getAll({
          organization_id: organizationId,
          page,
          page_size: 100,
        });

        allTasks.push(
          ...response.items.map(task => ({
            id: task.id,
            agent_id: task.agent_id,
          }))
        );
        totalPagesToFetch = Math.max(1, response.total_pages || 1);
        page += 1;
      } while (page <= totalPagesToFetch);

      return allTasks
        .filter(
          task => task.agent_id !== null && agentIds.includes(task.agent_id)
        )
        .map(task => task.id);
    },
    []
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [currentOrganization]);

  useEffect(() => {
    if (!currentOrganization) {
      return;
    }

    loadCategories();
    loadData();
  }, [currentOrganization, loadData, loadCategories]);

  const loadCrewForEditing = async (crew: Crew) => {
    // Verificar se agent_ids existe e tem dados
    if (!crew.agent_ids || crew.agent_ids.length === 0) {
      setEditingWorkflow({
        agents: [],
        tasks: [],
        connections: [],
        crewName: crew.name,
        crewDescription: crew.description || '',
        category_ids: crew.category_ids || [],
      });
      return;
    }

    try {
      // Buscar dados completos dos agentes usando os agent_ids
      const agentsData = await Promise.all(
        crew.agent_ids.map(agentId => agentService.getById(agentId))
      );

      // Buscar ordem de execução existente
      let executionOrder: Array<{ agent_id: number; execution_order: number }> =
        [];
      try {
        const orderResponse = await crewOrderService.getAgentExecutionOrder(
          crew.id
        );
        // Garantir que executionOrder seja sempre um array
        executionOrder = Array.isArray(orderResponse) ? orderResponse : [];
      } catch (error) {
        executionOrder = [];
      }

      // Converter agentes para o formato do WorkflowBuilder
      const agents = agentsData
        .filter(agent => agent) // Filtrar agentes válidos
        .map((agent, index) => {
          // Usar ordem existente se disponível, senão usar posição sequencial
          const order = executionOrder.find(o => o.agent_id === agent.id);
          const position = order ? order.execution_order : index;

          return {
            id: `agent_${agent.id}`,
            type: 'agent' as const,
            name: agent.name,
            description: agent.backstory || '',
            role: agent.role || '',
            goal: agent.goal || '',
            x: 50 + position * 250,
            y: 50,
            connections: [],
            agentId: agent.id,
          };
        });

      // Criar conexões baseadas na ordem de execução existente
      const connections: Array<{ from: string; to: string }> = [];

      if (executionOrder.length > 1) {
        // Criar conexões sequenciais baseadas na ordem de execução
        for (let i = 0; i < executionOrder.length - 1; i++) {
          const currentAgent = agents.find(
            agent => agent.agentId === executionOrder[i].agent_id
          );
          const nextAgent = agents.find(
            agent => agent.agentId === executionOrder[i + 1].agent_id
          );

          if (currentAgent && nextAgent) {
            connections.push({
              from: currentAgent.id,
              to: nextAgent.id,
            });
          }
        }
      }

      const workflowData = {
        agents,
        tasks: [], // Apenas agentes por enquanto
        connections,
        crewName: crew.name,
        crewDescription: crew.description,
        category_ids: crew.category_ids || [],
      };

      setEditingWorkflow(workflowData);
    } catch (error) {
      toast.error(t('teams.errorLoadingAgents'));
      setEditingWorkflow({
        agents: [],
        tasks: [],
        connections: [],
        crewName: crew.name,
        crewDescription: crew.description || '',
        category_ids: crew.category_ids || [],
      });
    }
  };

  const handleWorkflowSave = async (workflow: {
    agents: any[];
    connections: { from: string; to: string }[];
    executionOrder?: Array<{ agent_id: number; execution_order: number }>;
    crewName?: string;
    crewDescription?: string;
    category_ids?: number[];
  }) => {
    if (!currentOrganization) {
      toast.error(t('teams.organizationNotFound'));
      return;
    }

    try {
      setIsCreating(true);

      // Extrair IDs dos agentes selecionados (não criar novos)
      const agentIds = workflow.agents
        .filter(agent => agent.agentId) // Apenas agentes com ID válido
        .map(agent => agent.agentId);

      if (agentIds.length === 0) {
        toast.error(t('teams.noValidAgentsSelected'));
        return;
      }

      const taskIds = await getTaskIdsForAgents(
        currentOrganization.id,
        agentIds
      );

      // Criar Equipe apenas vinculando agentes e tasks existentes
      const crew = await crewService.create({
        name:
          workflow.crewName ||
          t('teams.teamCreatedWithAgentsDefault', { count: agentIds.length }),
        description:
          workflow.crewDescription ||
          t('teams.teamCreatedWithAgents', { count: agentIds.length }),
        organization_id: currentOrganization.id,
        agent_ids: agentIds,
        task_ids: taskIds,
        category_ids: workflow.category_ids || [],
        public: false,
        hidden: false,
      });

      // Salvar ordem de execução se houver
      if (
        workflow.executionOrder &&
        workflow.executionOrder.length > 0 &&
        crew.id
      ) {
        try {
          console.log(
            'Tentando salvar ordem de execução para crew ID:',
            crew.id
          );
          await crewOrderService.updateAgentExecutionOrder(
            crew.id,
            workflow.executionOrder
          );
        } catch (error) {
          console.error('Erro ao salvar ordem de execução:', error);
          toast.error(t('teams.executionOrderError'));
        }
      } else if (
        workflow.executionOrder &&
        workflow.executionOrder.length > 0 &&
        !crew.id
      ) {
        console.error(
          'Crew ID é undefined, não é possível salvar ordem de execução'
        );
        toast.error('Erro: ID da equipe não foi retornado pela API');
      }

      await loadData();
      setShowCreateModal(false);
      toast.success(t('teams.teamCreatedSuccessfully'));
    } catch (error) {
      console.error('Erro ao criar Equipe:', error);
      toast.error(t('teams.errorCreatingTeam'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCrewClick = (id: number) => {
    setCrewToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteCrew = async () => {
    if (!crewToDelete) return;

    try {
      await crewService.delete(crewToDelete);
      setCrews(prev => prev.filter(crew => crew.id !== crewToDelete));
      toast.success(t('teams.teamRemovedSuccessfully'));
      setShowDeleteModal(false);
      setCrewToDelete(null);
    } catch (error) {
      toast.error(t('teams.errorRemovingTeam'));
    }
  };

  const handleTogglePublic = async (crew: Crew) => {
    if (!isSuperAdmin()) {
      toast.error(t('teams.onlySuperAdminCanChangePublic'));
      return;
    }

    try {
      const updatedCrew = await crewService.update(crew.id, {
        public: !crew.public,
      });
      setCrews(prev => prev.map(c => (c.id === crew.id ? updatedCrew : c)));
      toast.success(
        updatedCrew.public ? t('teams.makePublic') : t('teams.makePrivate')
      );
    } catch (error) {
      toast.error(t('teams.errorUpdatingTeam'));
    }
  };

  const handleToggleHidden = async (crew: Crew) => {
    try {
      // Garantir que o valor seja boolean (tratar undefined/null como false)
      const currentValue = Boolean(crew.hidden);
      const newValue = !currentValue;

      const updatedCrew = await crewService.update(crew.id, {
        hidden: newValue,
      });

      setCrews(prev => prev.map(c => (c.id === crew.id ? updatedCrew : c)));
      toast.success(
        updatedCrew.hidden
          ? t('teams.hideInConversation')
          : t('teams.showInConversation')
      );
    } catch (error) {
      toast.error(t('teams.errorUpdatingTeam'));
    }
  };

  const handleEditCrew = async (crew: Crew) => {
    setEditingCrew(crew);
    setShowEditModal(true);
    await loadCrewForEditing(crew);
  };

  const handleUpdateCrew = async () => {
    if (!editingCrew) return;

    try {
      const updatedCrew = await crewService.update(editingCrew.id, {
        name: editingCrew.name,
        description: editingCrew.description,
        agent_ids: editingCrew.agent_ids,
        task_ids: editingCrew.task_ids,
        public: editingCrew.public,
        hidden: editingCrew.hidden,
      });
      setCrews(prev =>
        prev.map(crew => (crew.id === editingCrew.id ? updatedCrew : crew))
      );
      setShowEditModal(false);
      setEditingCrew(null);
      toast.success(t('teams.teamUpdatedSuccessfully'));
    } catch (error) {
      toast.error(t('teams.errorUpdatingTeam'));
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge variant="success">{t('teams.active')}</Badge>
    ) : (
      <Badge variant="error">{t('teams.inactive')}</Badge>
    );
  };

  const filteredCrews = crews.filter(
    crew =>
      (crew.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (crew.description?.toLowerCase() || '').includes(
        searchQuery.toLowerCase()
      ) ||
      (crew.categories || []).some(category =>
        (category.name || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const handleModalClose = () => {
    setShowCreateModal(false);
    resetData();
  };

  return (
    <div className="space-y-6">
      {/* Actions and Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 min-w-64">
          <Input
            placeholder={t('teams.searchTeams')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            leftIcon={<Users className="h-5 w-5" />}
          />
        </div>
        <div className="w-64">
          <select
            className="select-control w-full"
            value={selectedCategoryId ?? ''}
            onChange={e =>
              setSelectedCategoryId(
                e.target.value ? parseInt(e.target.value, 10) : null
              )
            }
          >
            <option value="">{t('teams.allCategories')}</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('teams.refresh')}
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('teams.newTeam')}
          </Button>
        </div>
      </div>

      {/* Crews List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-white">
            {t('navigation.agentTeams')} ({filteredCrews.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <LoadingSpinner />
            </div>
          ) : filteredCrews.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('teams.noTeamsFound')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t('teams.createFirstTeam')}
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('teams.createTeam')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCrews.map(crew => (
                <Card key={crew.id} className="flex flex-col h-full">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg text-gray-900 dark:text-white">
                          {crew.name}
                        </CardTitle>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          ID: {crew.id}
                        </p>
                      </div>
                      {getStatusBadge(crew.is_active)}
                    </div>
                    <CardContent className="p-0">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {crew.description}
                      </p>
                    </CardContent>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <div className="space-y-2 text-sm flex-1">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          {t('teams.agents')}
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {crew.agent_ids?.length ||
                            crew.crew_agents?.length ||
                            0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          {t('teams.tasks')}
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {crew.task_ids?.length ||
                            crew.crew_tasks?.length ||
                            0}
                        </span>
                      </div>
                      <div className="pt-1">
                        <div className="flex flex-wrap gap-1">
                          {(crew.categories || []).map(category => (
                            <Badge key={category.id} variant="secondary" size="sm">
                              {category.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2 mt-4">
                      {isSuperAdmin() && (
                        <Button
                          size="sm"
                          variant={crew.public ? 'primary' : 'outline'}
                          onClick={() => handleTogglePublic(crew)}
                          title={
                            crew.public
                              ? t('teams.makePrivate')
                              : t('teams.makePublic')
                          }
                        >
                          <Globe className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={crew.hidden ? 'outline' : 'outline'}
                        onClick={() => handleToggleHidden(crew)}
                        title={
                          crew.hidden
                            ? t('teams.showInConversation')
                            : t('teams.hideInConversation')
                        }
                        className={crew.hidden ? 'opacity-50' : ''}
                      >
                        {crew.hidden ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditCrew(crew)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteCrewClick(crew.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {totalCrews > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {totalCrews} {t('navigation.agentTeams').toLowerCase()}
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Create Crew Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={handleModalClose}
        title={t('teams.createNewTeam')}
        size="full"
      >
        <SimpleWorkflowBuilder
          onSave={handleWorkflowSave}
          onCancel={handleModalClose}
          isEditing={false}
          availableCategories={categories}
          organizationId={currentOrganization?.id}
          onCategoriesUpdated={loadCategories}
        />
      </Modal>

      {/* Edit Crew Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingCrew(null);
        }}
        title={t('teams.editTeam')}
        size="full"
      >
        <div className="h-full flex flex-col">
          {/* Workflow Builder Section */}
          <div className="flex-1 overflow-hidden">
            {editingWorkflow ? (
              <SimpleWorkflowBuilder
                initialData={editingWorkflow}
                crewId={editingCrew?.id}
                isEditing={true}
                availableCategories={categories}
                organizationId={currentOrganization?.id}
                onCategoriesUpdated={loadCategories}
                onSave={async workflow => {
                  if (!editingCrew) return;

                  try {
                    // Extrair IDs dos agentes selecionados (não criar novos)
                    const agentIds = workflow.agents
                      .filter(agent => agent.agentId)
                      .map(agent => agent.agentId)
                      .filter((id): id is number => id !== undefined);

                    if (agentIds.length === 0) {
                      toast.error(t('teams.noValidAgentsSelected'));
                      return;
                    }

                    if (!currentOrganization) {
                      toast.error(t('teams.organizationNotFound'));
                      return;
                    }

                    const taskIds = await getTaskIdsForAgents(
                      currentOrganization.id,
                      agentIds
                    );

                    // Atualizar Equipe apenas vinculando agentes e tasks existentes
                    await crewService.update(editingCrew.id, {
                      name: workflow.crewName || editingCrew.name,
                      description:
                        workflow.crewDescription || editingCrew.description,
                      agent_ids: agentIds,
                      task_ids: taskIds,
                      category_ids: workflow.category_ids || [],
                      public: editingCrew.public,
                      hidden: editingCrew.hidden,
                    });

                    // Salvar ordem de execução se houver
                    if (
                      workflow.executionOrder &&
                      workflow.executionOrder.length > 0
                    ) {
                      try {
                        await crewOrderService.updateAgentExecutionOrder(
                          editingCrew.id,
                          workflow.executionOrder
                        );
                      } catch (error) {
                        console.error(
                          'Erro ao atualizar ordem de execução:',
                          error
                        );
                        toast.error(t('teams.executionOrderUpdateError'));
                      }
                    }

                    await loadData();
                    setShowEditModal(false);
                    setEditingCrew(null);
                    setEditingWorkflow(null);
                    toast.success(t('teams.teamUpdatedSuccessfully'));
                  } catch (error) {
                    console.error('Erro ao atualizar Equipe:', error);
                    toast.error(t('teams.errorUpdatingTeam'));
                  }
                }}
                onCancel={() => {
                  setShowEditModal(false);
                  setEditingCrew(null);
                  setEditingWorkflow(null);
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCrewToDelete(null);
        }}
        onConfirm={handleDeleteCrew}
        message={t('teams.confirmDeleteTeam')}
        variant="danger"
      />
    </div>
  );
};

// Wrapper component with context provider
const AgentTeamsWithProvider: React.FC = () => {
  return (
    <CrewCreationProvider>
      <AgentTeams />
    </CrewCreationProvider>
  );
};

export default AgentTeamsWithProvider;
